import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { formatDate, formatViews } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import Layout, { Spinner } from "../components/Layout";
import { SectionHeader } from "../components/SectionBlock";
import { PlayCircle, Eye } from "lucide-react";

const FALLBACK = "/brand/logo-512.png";

function VideoCard({ a }) {
  const hi = a.language === "hi";
  return (
    <Link to={`/article/${a.slug}`} data-testid={`video-card-${a.slug}`} className="group block bg-white">
      <div className="relative overflow-hidden aspect-video bg-black">
        <img src={a.featured_image || FALLBACK} alt={a.title} loading="lazy" onError={(e) => { e.target.src = FALLBACK; }}
          className="card-img w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="bg-r24-red/90 text-white w-14 h-14 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <PlayCircle size={32} />
          </span>
        </div>
        {a.location && <span className="absolute top-2 left-2 bg-black/80 text-white text-[10px] font-bold uc px-2 py-0.5">{a.location}</span>}
      </div>
      <div className="pt-3">
        <h3 className={`font-head font-bold text-lg leading-tight link-head ${hi ? "lang-hi" : ""}`}>{a.title}</h3>
        <div className="text-gray-500 text-[11px] mt-2 font-mono2 flex items-center gap-3">
          <span>{formatDate(a.published_at, a.language)}</span>
          {formatViews(a.views) && <span className="flex items-center gap-1"><Eye size={12} /> {formatViews(a.views)}</span>}
        </div>
      </div>
    </Link>
  );
}

function VideoSection({ title, titleHi, items }) {
  if (!items?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 py-6">
      <SectionHeader title={title} titleHi={titleHi} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
        {items.map((a) => <VideoCard key={a.slug} a={a} />)}
      </div>
    </section>
  );
}

const UP_HINT = /uttar pradesh|kanpur|prayagraj|varanasi|ayodhya|gorakhpur|agra|meerut|noida|ghaziabad|bareilly|moradabad|jhansi|mathura|aligarh|sitapur|barabanki|unnao|sultanpur|rae bareli/i;

export default function VideoNews() {
  const [items, setItems] = useState(null);
  useSeo({ title: "Video News", description: "Rapid24News Video — Lucknow, Uttar Pradesh, Crime, Scam & India video news. Fast, Trusted, Fearless." });

  useEffect(() => {
    window.scrollTo(0, 0);
    client.get("/articles", { params: { category: "video", limit: 60 } })
      // ABSOLUTE VIDEO RULE: only stories with a genuine, playable video are ever shown here.
      .then((r) => setItems((r.data.items || []).filter((a) => a.video_url)));
  }, []);

  if (!items) return <Layout><Spinner /></Layout>;

  const has = (a, re) => re.test(`${a.location} ${a.district} ${(a.tags || []).join(" ")} ${a.title}`);
  const lucknow = items.filter((a) => /lucknow|लखनऊ/i.test(`${a.location} ${a.title}`));
  const up = items.filter((a) => !lucknow.includes(a) && has(a, UP_HINT));
  const crime = items.filter((a) => /crime|scam|fraud|corruption|क्राइम|घोटाला|ठगी|हत्या/i.test(`${(a.tags || []).join(" ")} ${a.title}`));
  const india = items.filter((a) => !lucknow.includes(a) && !up.includes(a));
  const mostWatched = [...items].sort((x, y) => (y.views || 0) - (x.views || 0)).slice(0, 8);

  return (
    <Layout>
      <div className="bg-r24-black text-white">
        <div className="max-w-7xl mx-auto px-4 py-8 flex items-center gap-3">
          <PlayCircle className="text-r24-red" size={36} />
          <div>
            <h1 className="font-head font-bold text-4xl md:text-5xl uc">Rapid<span className="text-r24-red">24</span> Video</h1>
            <p className="font-hindi text-gray-400">वीडियो न्यूज़ — लखनऊ · उत्तर प्रदेश · क्राइम · देश</p>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="max-w-3xl mx-auto px-4 py-24 text-center text-gray-500" data-testid="video-empty">No video news available right now. Please check back soon.</div>
      ) : (
        <>
          <VideoSection title="Latest Video News" titleHi="ताज़ा वीडियो" items={items.slice(0, 8)} />
          <VideoSection title="Lucknow Video" titleHi="लखनऊ वीडियो" items={lucknow} />
          <VideoSection title="Uttar Pradesh Video" titleHi="यूपी वीडियो" items={up} />
          <VideoSection title="Crime / Scam Video" titleHi="क्राइम · घोटाला वीडियो" items={crime} />
          <VideoSection title="India Video" titleHi="देश वीडियो" items={india} />
          <div className="bg-gray-50 border-y border-gray-200">
            <VideoSection title="Most Watched" titleHi="सबसे ज़्यादा देखे गए" items={mostWatched} />
          </div>
        </>
      )}
    </Layout>
  );
}
